import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { RefreshCw, ExternalLink, Loader2, Facebook, Linkedin, MessageSquare, ChevronDown, ChevronUp, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/og-image`;

/**
 * Social Preview tab — simplified.
 * Out-of-the-box, every listing's share preview uses:
 *   • title = "{Business Name} — Visit My Verified Page"
 *   • image = the listing's logo (or the LocalBest banner if no logo)
 * No setup required. This tab just SHOWS the preview and lets the owner
 * refresh the social cache after big logo/name changes. Advanced override
 * fields are tucked behind a single accordion.
 */
export default function SocialPreviewTab({ slug }) {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [form, setForm] = useState({ og_title: "", og_description: "", og_image: "" });
  const [imgVersion, setImgVersion] = useState(0);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/listings/${slug}/social-preview`, { headers: getAuthHeader() })
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
        setForm({
          og_title: r.data?.editable?.og_title || "",
          og_description: r.data?.editable?.og_description || "",
          og_image: r.data?.editable?.og_image || "",
        });
      })
      .catch((err) => toast.error(err?.response?.data?.detail || "Failed to load preview"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [slug, getAuthHeader]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        og_title: form.og_title || null,
        og_description: form.og_description || null,
        og_image: form.og_image || null,
      };
      await axios.patch(`${API}/listings/${slug}/social-preview`, body, { headers: getAuthHeader() });
      toast.success("Saved");
      setImgVersion((n) => n + 1);
      const r = await axios.get(`${API}/listings/${slug}/social-preview`, { headers: getAuthHeader() });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/listings/${slug}/social-preview/refresh`, {}, { headers: getAuthHeader() });
      toast.success("Cache refresh requested. FB/LinkedIn will refetch within minutes.");
      setImgVersion((n) => n + 1);
      const r = await axios.get(`${API}/listings/${slug}/social-preview`, { headers: getAuthHeader() });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed");
    } finally { setRefreshing(false); }
  };

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="social-preview-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[#7C3AED]" />
        <span className="ml-2 text-[#94A3B8]">Loading preview…</span>
      </div>
    );
  }

  const { resolved, canonical_url } = data;
  const liveTitle = (form.og_title && form.og_title.trim()) || resolved.title;
  const liveDescription = (form.og_description && form.og_description.trim()) || resolved.description;
  const liveImage = (form.og_image && form.og_image.trim()) || resolved.image;
  const imgSrc = `${liveImage}${liveImage.includes("?") ? "&" : "?"}cb=${imgVersion}`;
  const domain = "localbest.ai";

  return (
    <div className="space-y-6" data-testid="social-preview-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-white font-['Outfit']">Social Share Preview</h2>
          <p className="text-[#94A3B8] mt-1 text-sm max-w-2xl">
            This is what people see when they share your listing on Facebook, iMessage, LinkedIn, Slack, X, or any other platform. The image is your logo and the headline updates with your business name automatically.
          </p>
        </div>
        <Button onClick={refresh} disabled={refreshing} variant="outline" className="border-white/10 bg-white/5 hover:bg-white/10 text-white" data-testid="social-refresh-btn">
          {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Refresh Social Cache
        </Button>
      </div>

      {/* --- Live previews --- */}
      <div className="grid md:grid-cols-2 gap-4" data-testid="social-preview-cards">
        <PlatformPreview platform="Facebook · LinkedIn" icon={Facebook} url={domain} imgSrc={imgSrc} title={liveTitle} description={liveDescription} testid="preview-fb-li" />
        <PlatformPreview platform="iMessage · Slack · SMS" icon={MessageSquare} url={domain} imgSrc={imgSrc} title={liveTitle} description={liveDescription} compact testid="preview-imessage" />
        <PlatformPreview platform="X (Twitter)" icon={Linkedin} url={domain} imgSrc={imgSrc} title={liveTitle} description={liveDescription} testid="preview-x" />
        <div className="rounded-lg bg-gradient-to-br from-[#F59E0B]/10 to-[#EC4899]/10 border border-[#F59E0B]/20 p-4 text-sm text-[#94A3B8]" data-testid="canonical-link-card">
          <p className="text-white font-medium mb-1">Your share link</p>
          <code className="block text-xs text-[#FBBF24] bg-black/30 rounded px-2 py-1.5 truncate" data-testid="canonical-link">{canonical_url}</code>
          <p className="text-xs mt-2 leading-relaxed">
            <strong className="text-white">Tip:</strong> After changing your logo or business name, click <em>Refresh Social Cache</em> above, then paste your link into <a href="https://developers.facebook.com/tools/debug/" target="_blank" rel="noopener noreferrer" className="text-[#FBBF24] underline">Facebook's debugger</a> and click "Scrape Again" so existing posts update too.
          </p>
        </div>
      </div>

      {/* --- Advanced (hidden by default) --- */}
      <div className="rounded-xl bg-[#0F172A] border border-white/10">
        <button
          onClick={() => setShowAdvanced((v) => !v)}
          className="w-full px-4 py-3 flex items-center justify-between text-left text-white hover:bg-white/[0.02] transition"
          data-testid="toggle-advanced-share"
        >
          <span className="flex items-center gap-2">
            <span className="text-sm font-semibold">Advanced overrides</span>
            <span className="text-[10px] uppercase tracking-wider text-[#64748B]">optional</span>
          </span>
          {showAdvanced ? <ChevronUp className="w-4 h-4 text-[#94A3B8]" /> : <ChevronDown className="w-4 h-4 text-[#94A3B8]" />}
        </button>
        {showAdvanced && (
          <div className="p-4 border-t border-white/5 space-y-3" data-testid="social-advanced-form">
            <p className="text-xs text-[#94A3B8]">Leave blank to use the automatic defaults (recommended).</p>
            <Field
              label="Custom share title"
              value={form.og_title}
              onChange={(v) => setForm({ ...form, og_title: v })}
              placeholder={resolved.title}
              testid="input-og-title"
            />
            <Field
              label="Custom share description"
              value={form.og_description}
              onChange={(v) => setForm({ ...form, og_description: v })}
              placeholder={resolved.description}
              multiline
              testid="input-og-description"
            />
            <div>
              <label className="text-sm text-white font-medium block mb-1">Custom share image URL</label>
              <Input
                value={form.og_image}
                onChange={(e) => setForm({ ...form, og_image: e.target.value })}
                placeholder={resolved.image}
                className="bg-black/30 border-white/10 text-white placeholder:text-[#475569]"
                data-testid="input-og-image"
              />
              <p className="text-xs text-[#64748B] mt-1">Leave blank to use your business logo automatically.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={save} disabled={saving} className="bg-[#7C3AED] hover:bg-[#8B5CF6] text-white" data-testid="save-social-preview-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Overrides
              </Button>
              <Button variant="outline" onClick={() => setForm({ og_title: "", og_description: "", og_image: "" })} className="border-white/10 bg-white/5 hover:bg-white/10 text-white" data-testid="reset-social-preview-btn">
                Clear
              </Button>
            </div>
          </div>
        )}
      </div>

      <a href={canonical_url} target="_blank" rel="noopener noreferrer" className="text-[#00E5FF] text-xs inline-flex items-center gap-1 hover:text-[#22D3EE]" data-testid="canonical-link-bottom">
        Open your public listing <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
}

function PlatformPreview({ platform, icon: Icon, url, imgSrc, title, description, compact = false, testid }) {
  return (
    <div className="rounded-xl bg-[#0F172A] border border-white/10 overflow-hidden" data-testid={testid}>
      <div className="px-4 py-2 border-b border-white/5 flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 text-[#94A3B8]" />
        <span className="text-xs text-[#94A3B8] font-medium">{platform}</span>
      </div>
      <div className={compact ? "flex" : ""}>
        <div className={compact ? "w-28 h-28 flex-shrink-0 bg-black/30 flex items-center justify-center" : "w-full aspect-[1200/630] bg-black/30 flex items-center justify-center"}>
          <img src={imgSrc} alt="Share preview" className="max-w-full max-h-full object-contain" onError={(e) => { e.currentTarget.style.opacity = 0.4; }} />
        </div>
        <div className="p-3 min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-[#64748B] truncate">{url}</p>
          <p className="text-white font-semibold text-sm leading-tight line-clamp-2 mt-1">{title}</p>
          <p className="text-[#94A3B8] text-xs line-clamp-2 mt-1">{description}</p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, multiline = false, testid }) {
  const Component = multiline ? "textarea" : "input";
  return (
    <div>
      <label className="text-sm text-white font-medium block mb-1">{label}</label>
      <Component
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={multiline ? 3 : undefined}
        className="w-full rounded-md bg-black/30 border border-white/10 text-white placeholder:text-[#475569] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#7C3AED]"
        data-testid={testid}
      />
    </div>
  );
}
